<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'NAVOnlineInvoiceInterfaceSettings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    companyId: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const formId = ref(null);
  const formName = ref('NAVOnlineInvoiceInterfaceSettings');
  const change_password = ref({
    password: null,
    verify_password: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    formId: formId.value,
    formName: formName.value,
    change_password: change_password.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = async () => {
    return $fn.value.fetch('read_nav_online_interface_setting', {
      parameters: { company_id: props.companyId },
      asResource: true,
    });
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'change_password' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      resource: $$load_resource,
      editor: $$load_editor,
      formId: $$load_formId,
    },
    ux: {},
    dataRefs: {
      resource,
      editor,
      formId,
      formName,
      change_password,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const payload = computed(() => {
    if (loaded.value) {
      return _.extend(
        {},
        editor.value.getData(),
        change_password.value.password && change_password.value.verify_password
          ? change_password.value
          : null
      );
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext(
            'NAVOnlineInvoiceInterfaceSettings|Title',
            'NAV online invoice interface configuration'
          ),
          resource.value.company_name,
        ],
        icon: $config.value.settings.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1141grhfi_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_script_uoye4lfct_value_callable = async (
    $event
  ) => {
    return resource.value.reset($event.data.response.data);
  };
  const act_1040167445267876_script_uoye4lfct_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_uoye4lfct_value_callable($event);
      },
    };
  };
  const inst_evh_1040167445267876_success_q7ky5xiur_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_1040167445267876_emit_0fc1qctf0_getActionArgs = async ($event) => {
    return {
      event: 'saved',
    };
  };
  const inst_evh_1040167445267876_success_atpxasuh9_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_1040167445267876_request_jgqpo18is_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_nav_online_interface_setting',
      data: payload.value,
    };
  };
  const act_2434514588044777_closeModal_jbgrx8d30_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_1141grhfi =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1141grhfi_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ic7gc4bx7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1141grhfi,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ic7gc4bx7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_o6xz9eleh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_a99nbw2of =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_o6xz9eleh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_a99nbw2of],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_vuvw2xoi4 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_gcjmltsuv =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_vuvw2xoi4,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_gcjmltsuv],
    event: 'close',
  });
  const act_1040167445267876_script_uoye4lfct = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_uoye4lfct_getActionArgs,
  });
  const inst_evh_1040167445267876_success_q7ky5xiur =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_uoye4lfct,
      event: 'success',
      when: inst_evh_1040167445267876_success_q7ky5xiur_getWhen,
      displayName: 'script',
    });
  const act_1040167445267876_emit_0fc1qctf0 = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_0fc1qctf0_getActionArgs,
  });
  const inst_evh_1040167445267876_success_atpxasuh9 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_0fc1qctf0,
      event: 'success',
      when: inst_evh_1040167445267876_success_atpxasuh9_getWhen,
      displayName: 'emit',
    });
  const act_1040167445267876_request_jgqpo18is = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_jgqpo18is_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_q7ky5xiur,
      inst_evh_1040167445267876_success_atpxasuh9,
    ],
  });
  const inst_evh_1040167445267876_submit_pijvi21sd =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_jgqpo18is,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_pijvi21sd],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_jbgrx8d30 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_jbgrx8d30_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_8r8kvqn81 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_jbgrx8d30,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_8r8kvqn81],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ic7gc4bx7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_a99nbw2of,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_gcjmltsuv,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_q7ky5xiur,
    inst_evh_1040167445267876_success_atpxasuh9,
    inst_evh_1040167445267876_submit_pijvi21sd,
    submit: inst_evh_1040167445267876_submit_pijvi21sd,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_8r8kvqn81,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ic7gc4bx7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ic7gc4bx7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_a99nbw2of = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_a99nbw2of.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_gcjmltsuv = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_gcjmltsuv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_q7ky5xiur = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_q7ky5xiur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_atpxasuh9 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_atpxasuh9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_pijvi21sd = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_pijvi21sd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_8r8kvqn81 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_8r8kvqn81.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="NAVOnlineInvoiceInterfaceSettings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="company_name"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Company name')
              "
              required
            >
              <BFormInput
                plaintext
                v-model="editor.company_name"
                :value="editor.company_name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.company_name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                class="fw-bold no-focus-border"
              >
              </BFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="base_url"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Base URL')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The root URL of the service.'
                )
              "
              required
            >
              <ItpFormInput
                name="base_url"
                v-model="editor.base_url"
                :value="editor.base_url"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.base_url =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="tax_number"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Tax number')
              "
              required
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The first 8 digits of the tax number.'
                )
              "
            >
              <ItpFormInput
                name="tax_number"
                v-model="editor.tax_number"
                :value="editor.tax_number"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.tax_number =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="login"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Login')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s login name.'
                )
              "
              required
            >
              <ItpFormInput
                name="login"
                v-model="editor.login"
                :value="editor.login"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.login =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password_hash"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Password hash')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password\'s SHA-512 hash.'
                )
              "
            >
              <ItpFormTextarea
                name="password_hash"
                v-model="resource.password"
                :value="resource.password"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (resource.password =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                readonly
              >
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Password')"
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password.'
                )
              "
            >
              <ItpFormInput
                type="password"
                name="password"
                v-model="change_password.password"
                :value="change_password.password"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (change_password.password =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Confirm password'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The generated password.'
                )
              "
            >
              <ItpFormInput
                type="password"
                name="verify_password"
                v-model="change_password.verify_password"
                :value="change_password.verify_password"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (change_password.verify_password =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="xml_sign_key"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'XML sign key')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s XML sign key.'
                )
              "
              required
            >
              <ItpFormInput
                name="xml_sign_key"
                v-model="editor.xml_sign_key"
                :value="editor.xml_sign_key"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.xml_sign_key =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="exchange_key"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'XML exchange key'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The technical user\'s XML exchange key.'
                )
              "
              required
            >
              <ItpFormInput
                name="exchange_key"
                v-model="editor.exchange_key"
                :value="editor.exchange_key"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.exchange_key =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="add_unknown_clients"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Save unknown clients.'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Add unknown client to the database.'
                )
              "
            >
              <ItpFormCheckbox
                name="add_unknown_clients"
                v-model="editor.add_unknown_clients"
                :value="editor.add_unknown_clients"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.add_unknown_clients =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="lookback_period"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Lookback period')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The number of days to be used to look back for invoices.'
                )
              "
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="lookback_period"
                v-model="editor.lookback_period"
                :value="editor.lookback_period"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.lookback_period =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
              <span class="input-group-text">
                {{ pgettext('InputGroupAddon', 'days') }}
              </span>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="request_timeout"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Request timeout')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Stop waiting for a response once the specified timeout has expired.'
                )
              "
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="request_timeout"
                v-model="editor.request_timeout"
                :value="editor.request_timeout"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.request_timeout =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
              <span class="input-group-text"> sec </span>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="auto_sync"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Auto sync')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Check to include this company to the automated sync job.'
                )
              "
            >
              <ItpFormCheckbox
                name="auto_sync"
                v-model="editor.auto_sync"
                :value="editor.auto_sync"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.auto_sync =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="pull_incoming_invoices"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Pull incoming invoices'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Enable pulling of incoming invoices from the agency.'
                )
              "
            >
              <ItpFormCheckbox
                name="pull_incoming_invoices"
                v-model="editor.pull_incoming_invoices"
                :value="editor.pull_incoming_invoices"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.pull_incoming_invoices =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="pull_outgoing_invoices"
              :label="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Pull outgoing invoices'
                )
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'Enable pulling of outgoing invoices from the agency.'
                )
              "
            >
              <ItpFormCheckbox
                name="pull_outgoing_invoices"
                v-model="editor.pull_outgoing_invoices"
                :value="editor.pull_outgoing_invoices"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.pull_outgoing_invoices =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="invoice_tag"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Invoice tag')
              "
              :helpTooltip="
                pgettext(
                  'NAVOnlineInvoiceInterfaceSettings',
                  'The tag to set on the imported invoices.'
                )
              "
            >
              <ItpFormInput
                name="invoice_tag"
                v-model="editor.invoice_tag"
                :value="editor.invoice_tag"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.invoice_tag =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
